// webpages/shared/scene-core/scene-schema.ts — the TypeScript shape of `scene.json`, matching
// `documentation/scene-schema.md`. THE single source of truth for the
// editor↔viewer contract: the editor PRODUCES this (webpages/editor/src/scene-doc.ts
// types its output against `SceneFile`) and viewer2 CONSUMES it
// (webpages/viewer2/src/scene/scene-types.ts is a thin re-export shim of this file). One
// definition, so the producer and the consumer can never silently drift — add a
// field on one side and the other side's build sees it immediately.
//
// Intentionally NO playcanvas / DOM imports so it stays portable across both
// build systems (editor = rollup, viewer2 = vite) and trivially shareable.
//
// ALL optional fields must degrade gracefully when missing — the viewer's
// loader applies sensible defaults from the schema doc's "Sensible defaults"
// table, and the editor omits a field rather than emitting nulls.

/** The scene.json `schemaVersion` this contract describes. Editor stamps it;
 *  viewer checks against it. Bumping the contract bumps this ONE constant and
 *  both sides move together. */
export const SUPPORTED_SCHEMA_VERSION = 1;

export type Vec3 = [number, number, number];

export type NavigationMode = 'rotating' | 'free';

export type Tonemap =
    | 'linear'
    | 'filmic'
    | 'hejl'
    | 'aces'
    | 'aces2'
    | 'neutral';

/** [frame, value, ...] — `frame` is integer; rest is the data per channel. */
export type Keyframe = number[];

export interface Transform {
    position?: Vec3;
    rotation?: Vec3;  // Euler degrees, XYZ order
    scale?: Vec3;
}

// ── Splat effects ─────────────────────────────────────────────────────────

export interface RadialWaveEffect {
    time?: Keyframe[];          // [[frame, value], ...]
    center?: Vec3;
    speed?: number;
    endRadius?: number;
    delay?: number;
    dotTint?: Vec3;
    liftTint?: Vec3;
    oscillation?: number;
    bandWidth?: number;
}

export interface ParticleRevealEffect {
    progress?: Keyframe[];      // [[frame, value], ...]
    spread?: number;
    direction?: Vec3;
    duration?: number;
    flash?: number;
}

export interface ScalarChannel {
    values: Keyframe[];         // [[frame, value], ...]
}

export interface CenteredScalarChannel extends ScalarChannel {
    center?: Vec3;
}

export interface SplatEffects {
    fade?: ScalarChannel;
    dissolve?: ScalarChannel;
    growScale?: ScalarChannel;
    revealRadius?: CenteredScalarChannel;
    particleReveal?: ParticleRevealEffect;
    radialWave?: RadialWaveEffect;
}

// ── Mesh effects ──────────────────────────────────────────────────────────

export interface ColorChannel {
    color: Keyframe[];          // [[frame, r, g, b], ...]
}

export interface EmissiveChannel {
    intensity?: Keyframe[];     // [[frame, v], ...]
    color?: Keyframe[];         // [[frame, r, g, b], ...]
}

export interface MeshEffects {
    opacity?: ScalarChannel;
    visible?: ScalarChannel;    // step interpolation, 0/1
    diffuse?: ColorChannel;
    emissive?: EmissiveChannel;
    metalness?: ScalarChannel;
    gloss?: ScalarChannel;
    bumpiness?: ScalarChannel;
    specularity?: ScalarChannel;
}

// ── Elements ──────────────────────────────────────────────────────────────

export interface SplatElement {
    id: string;
    url: string;                // .ply / .spz / .sog / .gsplat-octree
    transform?: Transform;
    effects?: SplatEffects;
}

export interface MeshElement {
    id: string;
    url: string;                // .glb / .gltf
    transform?: Transform;
    effects?: MeshEffects;
}

// ── Top-level groups ──────────────────────────────────────────────────────

export interface HdriConfig {
    url: string;
    showSkybox?: boolean;
}

export interface BloomConfig {
    enabled?: boolean;
    intensity?: number;         // 0..0.3
}

export interface ColorEnhanceConfig {
    enabled?: boolean;
    shadows?: number;
    highlights?: number;
    midtones?: number;
    vibrance?: number;
    dehaze?: number;
}

export interface LightingConfig {
    tonemap?: Tonemap;
    exposure?: number;          // EV stops
    contrast?: number;
    bloom?: BloomConfig;
    colorEnhance?: ColorEnhanceConfig;
}

export interface CameraInitial {
    position: Vec3;
    target: Vec3;
    fov?: number;
    /**
     * True when the author deliberately framed this view (editor "Set to
     * current view"), as opposed to the export falling back to wherever the
     * editor camera happened to be. Orbit mode honours `target` as the pivot
     * only when this is set — otherwise an unframed/legacy target can sit
     * metres off the splat and orbit empty space.
     */
    explicit?: boolean;
}

export interface CameraPose {
    frame: number;
    position: Vec3;
    target: Vec3;
    fov?: number;
}

/**
 * Per-scene orbit-camera behavior (navigation 'rotating' — drone/exterior
 * scenes). All fields optional; the viewer falls back to polished defaults
 * for rotating scenes (pitchMinDeg 16, zoom band 0.5–2× scene radius,
 * auto-rotate on) and keeps loose limits for the orbit fallback of 'free'
 * scenes with no walk block.
 */
export interface OrbitConfig {
    /** Elevation floor in degrees above the horizon — the camera never dips
     *  below it (16 ≈ the reference drone-viewer feel; 0 allows eye level;
     *  negative allows under-ground views). */
    pitchMinDeg?: number;
    /** Closest zoom in WORLD METERS (authored via capture-from-view). When
     *  present (>0) the viewer uses it verbatim — preferred over factors,
     *  which proved fragile on drone scenes whose bounding radius is inflated
     *  by outlier floaters. */
    zoomMinM?: number;
    /** Farthest zoom in world meters. */
    zoomMaxM?: number;
    /** LEGACY closest zoom as a multiple of scene radius (e.g. 0.5). Used
     *  only when zoomMinM is absent. */
    zoomMinFactor?: number;
    /** LEGACY farthest zoom as a multiple of scene radius (e.g. 2). */
    zoomMaxFactor?: number;
    /** Slowly orbit the pivot while the user is idle. Default true for
     *  rotating scenes. */
    autoRotate?: boolean;
    /** Auto-rotation speed in degrees per second. Default 3. */
    autoRotateSpeed?: number;
    /** Seconds of inactivity before auto-rotation starts. Default 5. */
    autoRotateDelay?: number;
    /** Scene radius (world units) MEASURED BY THE EDITOR at save time — the
     *  same number its capture-from-view buttons divided by. The viewer must
     *  use this for the zoom band when present; recomputing radius viewer-side
     *  can disagree (world vs local bounds under rotation/scale) and land the
     *  authored limits at the wrong distances. */
    sceneRadius?: number;
}

export interface CameraConfig {
    initial?: CameraInitial;
    poses?: CameraPose[];
    orbit?: OrbitConfig;
}

export interface Marker {
    id: string;
    position: Vec3;
    title: string;
    body?: string;
}

/** An additional walkable floor above/below the ground floor (flat, at `height`). */
export interface WalkLevel {
    /** Floor height, world Y (the ground floor sits at 0 after leveling). */
    height: number;
    /** Walkable polygon of THIS level in world XZ (closed; first point not repeated). */
    boundary: [number, number][];
    /** Optional author label ("Mezzanine"). Metadata only. */
    name?: string;
}

/**
 * A stair/ramp region connecting two heights. The footprint is a quad in
 * world XZ: the edge quad[0]→quad[1] sits at `lowHeight`, the opposite edge
 * quad[2]→quad[3] at `highHeight`, with the floor ramping linearly between
 * them. Draw it overlapping (or touching) the two floors it connects so the
 * camera can step on/off within the viewer's step tolerance (0.45 m).
 */
export interface WalkStairs {
    /** Exactly 4 XZ points in polygon (winding) order forming a SIMPLE quad.
     *  Edge 0→1 is the low edge (at lowHeight); the opposite edge 2→3 is the
     *  high edge (at highHeight). */
    quad: [number, number][];
    lowHeight: number;
    highHeight: number;
}

/**
 * Voxel collision for walking (splat-transform / LichtFeld `.voxel.json` +
 * `.voxel.bin` sparse octree). When present, the floor under the feet comes
 * from the voxels (column scan within the 0.45 m step tolerance) and the
 * voxels block a standing body; the walk planes and boundaries above still
 * decide where you may walk. Absent → plane-only walking, as before.
 */
export interface WalkVoxels {
    /** The `.voxel.json` URL (bundle-relative); its `.voxel.bin` sits beside
     *  it with the same name. */
    url: string;
    /** Voxel frame → world. Same convention as splat transforms (Euler
     *  degrees, XYZ, as PlayCanvas `setLocalEulerAngles`). Absent → identity. */
    transform?: Transform;
}

export interface WalkConfig {
    /** Ground-floor plane: camera height = plane height under XZ + eyeHeight (tilt-aware). */
    floor: { origin: Vec3; normal: Vec3 };
    /** Ground-floor walkable polygon in world XZ (closed; first point not repeated). */
    boundary: [number, number][];
    /** Camera height above the floor, world units. Default 1.6. */
    eyeHeight?: number;
    /** Additional walkable levels (upper floors, mezzanines, sunken areas).
     *  Optional; absent → single-floor scene. Viewers that predate this field
     *  ignore it and walk the ground floor only. */
    levels?: WalkLevel[];
    /** Stair/ramp connectors between floor heights. Optional. */
    stairs?: WalkStairs[];
    /** Voxel collision: floor height + walls from voxels. Optional; viewers
     *  that predate it walk the planes only. */
    voxels?: WalkVoxels;
}

export interface SceneFile {
    schemaVersion: number;
    name: string;
    /** Control mode. Absent → 'free' (cursor-look + WASD); 'rotating' = orbit. */
    navigation?: NavigationMode;
    splats?: SplatElement[];
    models?: MeshElement[];
    hdri?: HdriConfig;
    lighting?: LightingConfig;
    camera?: CameraConfig;
    markers?: Marker[];
    walk?: WalkConfig;
    /** Cover thumbnail URL (bundle-relative, e.g. "thumbnail.jpg"). Metadata
     *  for scene listings; the viewer does not render it. */
    thumbnail?: string;
    /** Short looping preview/hover video URL (bundle-relative, e.g.
     *  "preview.mp4"). Listing metadata for gallery hover-play (Netflix-style);
     *  the viewer does not render it. Its first frame should match `thumbnail`
     *  so the hover→play transition is seamless. */
    previewVideo?: string;
}
